import { z } from 'zod';

import { AlphaNumericRegex } from 'src/constants/validationConsts';
import { SubscriptionInterval } from 'src/routes/payments/subscriptions/types/subscriptions';
import {
  LineItemType,
  MAX_LINE_ITEM_AMOUNT,
  MAX_LINE_ITEM_QUANTITY,
} from 'src/services/api/invoicesApi';
import { PriceType } from 'src/services/api/productsApi';

const oneTimePriceSchema = (currencyPrefix?: string) =>
  z.object({
    type: z.literal(PriceType.OneTime),
    amount: z.string({ required_error: 'Price cannot be empty' }).refine(
      (value) => {
        const number = parseFloat(value);
        return number > 0 && number <= MAX_LINE_ITEM_AMOUNT;
      },
      {
        message: `Price must be greater than ${currencyPrefix}0 and at most ${currencyPrefix}100,000`,
      },
    ),
    currency: z.string(),
  });

const recurringPriceSchema = (currencyPrefix?: string) =>
  z.object({
    type: z.literal(PriceType.Recurring),
    amount: z.string({ required_error: 'Price cannot be empty' }).refine(
      (value) => {
        const number = parseFloat(value);
        return number > 0 && number <= MAX_LINE_ITEM_AMOUNT;
      },
      {
        message: `Price must be greater than ${currencyPrefix}0 and at most ${currencyPrefix}100,000`,
      },
    ),
    currency: z.string(),

    interval: z.enum(
      [
        SubscriptionInterval.Day,
        SubscriptionInterval.Week,
        SubscriptionInterval.Month,
        SubscriptionInterval.Quarterly,
        SubscriptionInterval.Biannually,
        SubscriptionInterval.Year,
        SubscriptionInterval.SemiAnnually,
      ],
      { required_error: 'Billing period must be selected' },
    ),
  });

export const serviceSchema = (currencyPrefix?: string) =>
  z.object({
    name: z
      .string({ required_error: 'Name required' })
      .refine(
        (val: string) => AlphaNumericRegex.test(val.replaceAll(' ', '')),
        'Name cannot be only numbers',
      ),
    description: z.string().optional(),
    imageUrls: z.array(z.string()),
    prices: z.array(
      z.discriminatedUnion('type', [
        oneTimePriceSchema(currencyPrefix),
        recurringPriceSchema(currencyPrefix),
      ]),
    ),
  });

export const serviceLineItemSchema = (currencyPrefix?: string) =>
  z.object({
    productId: z.string(),
    type: z.literal(LineItemType.Product),
    priceId: z.string(),
    id: z.string(),
    quantity: z.string({ required_error: 'Quantity is required' }).refine(
      (value) => {
        const parsedQuantity = parseFloat(value);
        return parsedQuantity >= 1 && parsedQuantity <= MAX_LINE_ITEM_QUANTITY;
      },
      { message: 'Specify quantity between 1 and 1,000,000' },
    ),
    amount: z
      .string({ required_error: 'Price is required' })
      .refine(
        (value) => {
          const number = parseFloat(value);
          return number >= 0;
        },
        { message: `Price cannot be negative` },
      )
      .refine(
        (value) => {
          const number = parseFloat(value);
          return number <= MAX_LINE_ITEM_AMOUNT;
        },
        { message: `Price can be at most ${currencyPrefix ?? '$'}100,000` },
      ),
    description: z.string({
      required_error: 'Provide a service',
    }),
    isEditing: z.boolean().optional(),
    isValid: z.boolean().optional(),
    shouldAddPrice: z.boolean().optional(),
  });

export type OneTimePriceSchema = z.infer<ReturnType<typeof oneTimePriceSchema>>;
export type RecurringPriceSchema = z.infer<
  ReturnType<typeof recurringPriceSchema>
>;
export type ServiceSchema = z.infer<ReturnType<typeof serviceSchema>>;
export const MIN_IMAGE_DIMENSION = 448;
export const MAX_IMAGE_DIMENSION = 4096;

export interface ProductPriceTableRowModal {
  id: string;
  amount: number;
  currency: string;
  type: string;
  intervalCount: number;
  interval: string;
  status: string;
  createdAt: string;
  isUsed: boolean;
}
